import React from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { darkModeClasses } from '../../utils/darkMode';
import { ChartDataPoint } from '../../types';

interface ActivityBreakdownChartProps {
  data: ChartDataPoint[];
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{payload[0].name}</p>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Count: {payload[0].value?.toLocaleString() || 0}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Percentage: {payload[0].percent?.toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 1 }) || '0%'}
        </p>
      </div>
    );
  }
  return null;
};

export const ActivityBreakdownChart: React.FC<ActivityBreakdownChartProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="text-center">
          <div className="text-gray-400 dark:text-gray-500 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm">No activity breakdown data available</p>
        </div>
      </div>
    );
  }

  // Aggregate data across all time periods
  const aggregatedData = data.reduce((acc, curr) => ({
    contributions: acc.contributions + (curr.contributions || 0),
    benefits: acc.benefits + (curr.benefits || 0),
    events: acc.events + (curr.events || 0),
    expenses: acc.expenses + (curr.expenses || 0)
  }), { contributions: 0, benefits: 0, events: 0, expenses: 0 });

  const pieData = [
    { name: 'Contributions', value: aggregatedData.contributions, color: '#10b981' },
    { name: 'Benefits', value: aggregatedData.benefits, color: '#f59e0b' },
    { name: 'Events', value: aggregatedData.events, color: '#3b82f6' },
    { name: 'Expenses', value: aggregatedData.expenses, color: '#ef4444' }
  ].filter(item => item.value > 0);

  if (pieData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 text-sm">No activities recorded</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <h4 className={`text-md font-semibold ${darkModeClasses.textPrimary} mb-4`}>Activity Breakdown</h4>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {pieData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};